// Admin Garment detail. Edit name + description + active, manage colourways
// (add/remove with hex picker) and size variants (add/remove with prices),
// upload + bind a mockup image.
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { useToast, ToastBanner } from '../../components/Toast'
import AiReferencesPanel from './AiReferencesPanel'
import { apiFetch, useApiSrc } from '../../lib/api'
import { AI_TOOLS_ENABLED } from '../../lib/features'
import { PrintAreaValue, DEFAULT_PRINT_AREA } from './PrintAreaEditor'
import PrintLocationsEditor, { PrintLocation } from './PrintLocationsEditor'
import EditorWizardBar from './EditorWizardBar'

interface Garment {
  id: string; slug: string; name: string; description: string | null
  isActive: boolean; sortOrder: number
  mockupUrl: string | null; mockupPath: string | null
  printArea: PrintAreaValue | null
  printLocations: PrintLocation[]
}
interface Colour {
  id: string; name: string; hex: string; sortOrder: number
  // Per-colour mockup. mockupFileId is the persisted ref; mockupUrl is the
  // server-signed URL the admin Mockup card uses to render previews.
  mockupFileId?: string | null
  mockupUrl?: string | null
}
interface Variant { id: string; size: string; sku: string; price: string; isActive: boolean; sortOrder: number }
// Per-(side × colour) mockup — most specific tier of the Designer's
// fallback chain. Keyed by (locationId, colourId).
interface SideColourMockup { locationId: string; colourId: string; fileId: string; url: string }
interface Resp { item: Garment; colours: Colour[]; variants: Variant[]; sideColourMockups: SideColourMockup[] }
// Upload-tier tag used by uploadMockupFor + the Mockup-card buttons to
// decide which API endpoint to hit. Three tiers: garment default,
// colour-only, and side × colour. Per-side-only mockups live on the
// PrintLocation JSON itself and are edited from PrintLocationsEditor.
type MockupTarget =
  | { kind: 'default' }
  | { kind: 'colour';     colourId: string }
  | { kind: 'sideColour'; locationId: string; colourId: string }

export default function GarmentDetail() {
  const { id } = useParams<{ id: string }>()
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [data, setData] = useState<Resp | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [editing, setEditing] = useState<Partial<Garment>>({})
  const [printLocations, setPrintLocations] = useState<PrintLocation[]>([])
  const [printAreaSaving, setPrintAreaSaving] = useState<'idle' | 'saving' | 'saved'>('idle')
  // legacy single-area state — preserved so we don't break older garments that
  // never got migrated. The save() call below writes both fields.
  const [printArea, setPrintArea] = useState<PrintAreaValue>(DEFAULT_PRINT_AREA)
  const fileRef = useRef<HTMLInputElement | null>(null)
  // Mockup-card view selectors. null on either axis = the default of
  // that axis (no side / no colour). The pair drives which tier of the
  // Designer fallback chain the upload button currently points at:
  //   (null, null)        → garment.mockupPath
  //   (locId, null)       → printLocations[locId].mockupFileId  *
  //   (null, colId)       → product_colours[colId].mockup_file_id
  //   (locId, colId)      → product_side_colour_mockups[loc,col]
  // * the per-location row is editable from PrintLocationsEditor; this
  //   card only displays it, with a small "configured below" note.
  const [mockupColourId, setMockupColourId]     = useState<string | null>(null)
  const [mockupLocationId, setMockupLocationId] = useState<string | null>(null)
  const { toast, show } = useToast()

  // New-colour + new-variant drafts (inline at bottom of each table).
  const [newColour, setNewColour] = useState<{ name: string; hex: string }>({ name: '', hex: '#0F1117' })
  const [newVariant, setNewVariant] = useState<{ size: string; price: string }>({ size: '', price: '' })

  async function load() {
    try {
      const d = await apiFetch<Resp>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}`)
      setData(d); setEditing({ name: d.item.name, description: d.item.description ?? '', isActive: d.item.isActive, sortOrder: d.item.sortOrder })
      setPrintArea(d.item.printArea ?? DEFAULT_PRINT_AREA)
      setPrintLocations(d.item.printLocations ?? [])
      setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { void load() /* eslint-disable-next-line */ }, [id])

  // Build the full page-state PATCH body. Both Save buttons send this
  // so the admin can't hit the wrong button and silently lose work —
  // historically "Save changes" only persisted the Settings card and
  // "Save locations" only persisted the Print-Locations card, which
  // led to lost calibrations when admins (rightly!) clicked the more
  // prominent left-column "Save changes" while editing the right
  // column.
  function buildFullPatchBody() {
    const locs = printLocations.map(({ mockupUrl, aiReferenceUrl, ...rest }) => { void mockupUrl; void aiReferenceUrl; return rest })
    return { ...editing, printArea, printLocations: locs }
  }

  async function saveTopLevel() {
    setSaving('saving'); setPrintAreaSaving('saving')
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}`, { method: 'PATCH', body: JSON.stringify(buildFullPatchBody()) })
      setSaving('saved'); setPrintAreaSaving('saved'); await load()
      show('Garment saved.')
    } catch (e) { setSaving('idle'); setPrintAreaSaving('idle'); show((e as Error).message, 'error', 5000) }
  }

  async function savePrintArea() {
    setPrintAreaSaving('saving'); setSaving('saving')
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}`, { method: 'PATCH', body: JSON.stringify(buildFullPatchBody()) })
      setPrintAreaSaving('saved'); setSaving('saved')
      await load()
      show(`Garment saved (${printLocations.length} location${printLocations.length === 1 ? '' : 's'}).`)
    } catch (e) { setPrintAreaSaving('idle'); setSaving('idle'); show((e as Error).message, 'error', 5000) }
  }

  async function uploadMockupFor(target: MockupTarget, e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return
    const reader = new FileReader()
    reader.onload = async () => {
      try {
        const up = await apiFetch<{ id: string }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
          method: 'POST',
          body: JSON.stringify({ originalName: f.name, mimeType: f.type, base64: reader.result, kind: 'mockup' }),
        })
        const tokenGetter = getAccessToken as unknown as () => Promise<string | undefined>
        if (target.kind === 'default') {
          await apiFetch(tokenGetter, `/api/admin/garments/${id}`, {
            method: 'PATCH', body: JSON.stringify({ mockupFileId: up.id }),
          })
        } else if (target.kind === 'colour') {
          await apiFetch(tokenGetter, `/api/admin/garments/${id}/colours/${target.colourId}`, {
            method: 'PATCH', body: JSON.stringify({ mockupFileId: up.id }),
          })
        } else {
          await apiFetch(tokenGetter, `/api/admin/garments/${id}/side-colour-mockup`, {
            method: 'PUT', body: JSON.stringify({
              locationId: target.locationId, colourId: target.colourId, fileId: up.id,
            }),
          })
        }
        await load()
      } catch (err) { alert((err as Error).message) }
    }
    reader.readAsDataURL(f)
    e.target.value = ''
  }

  async function addColour() {
    if (!newColour.name || !/^#[0-9a-fA-F]{6}$/.test(newColour.hex)) { alert('Name + valid #RRGGBB required'); return }
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}/colours`, { method: 'POST', body: JSON.stringify({ ...newColour, sortOrder: data?.colours.length ?? 0 }) })
      setNewColour({ name: '', hex: '#0F1117' })
      await load()
    } catch (e) { alert((e as Error).message) }
  }
  async function removeColour(cid: string) {
    if (!confirm('Remove this colour?')) return
    try { await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}/colours/${cid}`, { method: 'DELETE' }); await load() }
    catch (e) { alert((e as Error).message) }
  }

  async function addVariant() {
    if (!newVariant.size || !newVariant.price) { alert('Size + price required'); return }
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}/variants`, { method: 'POST', body: JSON.stringify({ size: newVariant.size.toUpperCase(), price: parseFloat(newVariant.price), sortOrder: data?.variants.length ?? 0 }) })
      setNewVariant({ size: '', price: '' })
      await load()
    } catch (e) { alert((e as Error).message) }
  }
  async function patchVariant(vid: string, body: { price?: number; isActive?: boolean }) {
    try { await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}/variants/${vid}`, { method: 'PATCH', body: JSON.stringify(body) }); await load() }
    catch (e) { alert((e as Error).message) }
  }
  async function removeVariant(vid: string) {
    if (!confirm('Remove this size?')) return
    try { await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/garments/${id}/variants/${vid}`, { method: 'DELETE' }); await load() }
    catch (e) { alert((e as Error).message) }
  }

  if (err) return <div className="p-8"><div className="card text-red-700">Error: {err}</div></div>
  if (!data) return <div className="p-8 text-ink-3 text-sm">Loading…</div>

  // Wizard "is this step done?" rules. Kept here (not in the bar
  // component) so the bar stays generic and re-usable across other
  // admin editors with different completion semantics.
  // Resolve the card preview URL once — reused in the preview img AND
  // passed to PrintLocationsEditor so the calibration box always shows
  // the same image as the card above, regardless of which tier was uploaded.
  const mockupActiveColour   = mockupColourId   ? data.colours.find((c) => c.id === mockupColourId)   : null
  const mockupActiveLocation = mockupLocationId ? printLocations.find((l) => l.id === mockupLocationId) : null
  const mockupScRow = (mockupLocationId && mockupColourId)
    ? data.sideColourMockups.find((m) => m.locationId === mockupLocationId && m.colourId === mockupColourId)
    : null
  const mockupSideUrl   = (!mockupScRow && mockupActiveLocation?.mockupFileId) ? mockupActiveLocation.mockupUrl : null
  const mockupColourUrl = (!mockupScRow && !mockupSideUrl && mockupActiveColour) ? mockupActiveColour.mockupUrl : null
  const mockupDefaultUrl = (!mockupScRow && !mockupSideUrl && !mockupColourUrl) ? data.item.mockupUrl : null
  // resolvedMockupUrl — the exact URL the card preview shows. Falls through
  // all uploaded tiers so it's never null when any mockup exists.
  const resolvedMockupUrl =
    mockupScRow?.url ?? mockupSideUrl ?? mockupColourUrl ?? mockupDefaultUrl
    ?? data.colours.find((c) => c.mockupUrl)?.mockupUrl
    ?? data.sideColourMockups[0]?.url
    ?? null
  const mockupTierLabel =
    mockupScRow      ? `${mockupActiveLocation!.name} × ${mockupActiveColour!.name}` :
    mockupSideUrl    ? `${mockupActiveLocation!.name} (all colours)` :
    mockupColourUrl  ? `${mockupActiveColour!.name} (all sides)` :
    'Default'

  const hasMockup    = !!data.item.mockupUrl
  const hasSettings  = !!(editing.name && (editing.name as string).trim().length > 0)
  const hasLocations = printLocations.length > 0 && printLocations.every(l => l.width > 0 && l.height > 0)
  const hasColours   = data.colours.length > 0
  const hasVariants  = data.variants.length > 0

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <EditorWizardBar
        title="Garment editor"
        steps={[
          { id: 'mockup',    label: 'Mockup',     done: hasMockup },
          { id: 'settings',  label: 'Settings',   done: hasSettings },
          { id: 'locations', label: 'Print areas',done: hasLocations },
          { id: 'colours',   label: 'Colours',    done: hasColours },
          { id: 'variants',  label: 'Sizes',      done: hasVariants },
          { id: 'save',      label: 'Save & finish', onClick: savePrintArea },
        ]}
      />
      <div className="mb-4"><Link to="/admin/garments" className="text-xs text-ink-3 hover:text-gold-dark">← All garments</Link></div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Garment</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">{data.item.name}</h1>
          <div className="text-xs text-ink-3 mt-1">slug: <span className="font-mono">{data.item.slug}</span></div>
        </div>
        <div className="text-xs text-ink-3">{saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved' : ''}</div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">

        {/* Mockup + meta */}
        <div className="space-y-6">
          <div id="wiz-mockup" className="card scroll-mt-24 transition-shadow">
            <div className="eyebrow">Mockup image</div>
            <div className="bg-surface border border-border rounded h-48 flex items-center justify-center overflow-hidden relative mb-3">
              {resolvedMockupUrl ? (
                <img src={apiSrc(resolvedMockupUrl)} alt="" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-sm text-ink-3 text-center px-3">No mockup for <strong className="text-ink-2">{mockupTierLabel}</strong></span>
              )}
              {(mockupActiveLocation || mockupActiveColour) && (
                <span className="absolute top-1.5 left-1.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white border border-border flex items-center gap-1.5">
                  {mockupActiveColour && <span className="w-3 h-3 rounded-full border border-ink-4 inline-block" style={{ background: mockupActiveColour.hex }} />}
                  <span>{mockupTierLabel}</span>
                </span>
              )}
            </div>
            {/* Side + Colour selectors — sit below the preview so the
                axis being edited is obvious before clicking Upload. */}
            <div className="space-y-2 mb-3">
              {printLocations.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-ink-3 w-12 shrink-0">Side</span>
                  <select value={mockupLocationId ?? ''}
                    onChange={(e) => setMockupLocationId(e.target.value || null)}
                    className="flex-1 text-xs px-2 py-1.5 bg-white border border-border rounded text-ink focus:outline-none focus:border-gold">
                    <option value="">— All sides (default mockup) —</option>
                    {printLocations.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}{l.mockupFileId ? ' ✓' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              {data.colours.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-ink-3 w-12 shrink-0">Colour</span>
                  <select value={mockupColourId ?? ''}
                    onChange={(e) => setMockupColourId(e.target.value || null)}
                    className="flex-1 text-xs px-2 py-1.5 bg-white border border-border rounded text-ink focus:outline-none focus:border-gold">
                    <option value="">— All colours —</option>
                    {data.colours.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.mockupUrl ? ' ✓' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {/* Upload / clear for the current (side, colour) combination */}
            {(() => {
              const target: MockupTarget =
                mockupLocationId && mockupColourId
                  ? { kind: 'sideColour', locationId: mockupLocationId, colourId: mockupColourId }
                  : mockupColourId
                    ? { kind: 'colour', colourId: mockupColourId }
                    : { kind: 'default' }
              const uploadLabel =
                target.kind === 'sideColour' ? `Upload — ${printLocations.find((l) => l.id === target.locationId)?.name} × ${data.colours.find((c) => c.id === target.colourId)?.name}` :
                target.kind === 'colour'     ? `Upload — ${data.colours.find((c) => c.id === target.colourId)?.name} (all sides)` :
                                               'Upload default mockup'
              const tokenGetter = getAccessToken as unknown as () => Promise<string | undefined>
              const existsAtTarget =
                target.kind === 'sideColour' ? !!data.sideColourMockups.find((m) => m.locationId === target.locationId && m.colourId === target.colourId) :
                target.kind === 'colour'     ? !!data.colours.find((c) => c.id === target.colourId)?.mockupUrl :
                                               !!data.item.mockupUrl
              async function clearTarget() {
                try {
                  if (target.kind === 'sideColour') {
                    await apiFetch(tokenGetter, `/api/admin/garments/${id}/side-colour-mockup?locationId=${encodeURIComponent(target.locationId)}&colourId=${encodeURIComponent(target.colourId)}`, { method: 'DELETE' })
                  } else if (target.kind === 'colour') {
                    await apiFetch(tokenGetter, `/api/admin/garments/${id}/colours/${target.colourId}`, { method: 'PATCH', body: JSON.stringify({ mockupFileId: null }) })
                  } else {
                    await apiFetch(tokenGetter, `/api/admin/garments/${id}`, { method: 'PATCH', body: JSON.stringify({ mockupFileId: null }) })
                  }
                  await load()
                } catch (err) { alert((err as Error).message) }
              }
              return (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadMockupFor(target, e)} />
                  <div className="flex gap-2">
                    <button onClick={() => fileRef.current?.click()} className="btn-outline flex-1 justify-center text-xs">
                      {uploadLabel}
                    </button>
                    {existsAtTarget && (
                      <button onClick={clearTarget} className="btn-outline text-xs px-3" title="Clear this mockup">×</button>
                    )}
                  </div>
                  <p className="text-[11px] text-ink-3 mt-2">
                    {target.kind === 'sideColour' ? (
                      <span>Shown when a customer views the <strong className="text-ink-2">{printLocations.find((l) => l.id === target.locationId)?.name}</strong> side in <strong className="text-ink-2">{data.colours.find((c) => c.id === target.colourId)?.name}</strong>. Falls back to colour → default.</span>
                    ) : target.kind === 'colour' ? (
                      <span>Shown for <strong className="text-ink-2">{data.colours.find((c) => c.id === target.colourId)?.name}</strong> on any side without a more specific image. Falls back to the default.</span>
                    ) : (
                      <span>Global fallback shown in the Designer when no side/colour mockup is set. Transparent PNG works best.</span>
                    )}
                  </p>
                </>
              )
            })()}
          </div>

          <div id="wiz-settings" className="card scroll-mt-24 transition-shadow">
            <div className="eyebrow">Settings</div>
            <div className="space-y-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Name</div><input value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input" /></div>
              <div><div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Description</div><textarea rows={3} value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="input" /></div>
              <div><div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">Sort order</div><input type="number" value={editing.sortOrder ?? 0} onChange={(e) => setEditing({ ...editing, sortOrder: parseInt(e.target.value) || 0 })} className="input" /></div>
              <label className="flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={editing.isActive ?? true} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} /> Active (visible to customers)</label>
              <button onClick={saveTopLevel} className="btn-gold w-full justify-center">Save changes</button>
            </div>
          </div>
        </div>

        {/* Colours + Variants + Print Area */}
        <div className="lg:col-span-2 space-y-6">
          <div id="wiz-locations" className="card scroll-mt-24 transition-shadow">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="eyebrow !mb-0">Print locations</div>
                <p className="text-[11px] text-ink-3 mt-0.5">Where the customer's artwork can land. Add one per side (Front, Back, sleeves, chest, etc.). Each can have its own mockup image.</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[11px] text-ink-3">{printAreaSaving === 'saving' ? 'Saving…' : printAreaSaving === 'saved' ? 'Saved' : ''}</span>
                <button onClick={savePrintArea} className="btn-gold text-xs px-3 py-1.5">Save locations</button>
              </div>
            </div>
            <PrintLocationsEditor
              productId={data.item.id}
              productMockupUrl={data.item.mockupUrl ? apiSrc(data.item.mockupUrl) : null}
              activeLocationId={mockupLocationId}
              colours={data.colours}
              sideColourMockups={data.sideColourMockups}
              locations={printLocations}
              onChange={setPrintLocations}
            />
          </div>

          <div id="wiz-colours" className="card scroll-mt-24 transition-shadow">
            <div className="flex items-baseline justify-between mb-3"><div className="eyebrow !mb-0">Colourways ({data.colours.length})</div></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.colours.map((c) => (
                <div key={c.id} className="flex items-center gap-2 p-2 border border-border rounded">
                  {/* Mockup thumbnail when set, otherwise the colour swatch.
                      Either way it's a quick at-a-glance indicator. */}
                  {c.mockupUrl ? (
                    <img src={apiSrc(c.mockupUrl)} alt=""
                      className="w-9 h-9 object-cover rounded border border-border bg-surface" />
                  ) : (
                    <span className="w-9 h-9 rounded-full border border-border" style={{ background: c.hex }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate flex items-center gap-1.5">
                      {c.name}
                      {c.mockupUrl && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-gold/15 text-gold-dark border border-gold/30">mockup</span>
                      )}
                    </div>
                    <div className="text-[11px] text-ink-3 font-mono">{c.hex}</div>
                  </div>
                  {/* Per-colour mockup picker. Each row owns its own
                      hidden input so clicks are unambiguous. */}
                  <label className="text-ink-3 hover:text-ink cursor-pointer" title={c.mockupUrl ? 'Replace mockup for this colour' : 'Upload mockup for this colour'}>
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadMockupFor({ kind: 'colour', colourId: c.id }, e)} />
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </label>
                  <button onClick={() => removeColour(c.id)} className="text-ink-3 hover:text-red-600 text-sm" title="Remove colour">×</button>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2 mt-4 pt-4 border-t border-border">
              <div className="flex-1"><div className="eyebrow">Name</div><input value={newColour.name} onChange={(e) => setNewColour({ ...newColour, name: e.target.value })} placeholder="Charcoal" className="input" /></div>
              <div><div className="eyebrow">Hex</div><input type="color" value={newColour.hex} onChange={(e) => setNewColour({ ...newColour, hex: e.target.value.toUpperCase() })} className="h-10 w-12 p-0 border border-border rounded cursor-pointer" /></div>
              <button onClick={addColour} className="btn-gold">+ Add</button>
            </div>
          </div>

          <div id="wiz-variants" className="card scroll-mt-24 transition-shadow">
            <div className="eyebrow">Sizes & pricing ({data.variants.length})</div>
            <table className="w-full text-sm">
              <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
                <tr>
                  <th className="text-left px-3 py-2">Size</th>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-center px-3 py-2">Active</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.variants.map((v) => (
                  <tr key={v.id}>
                    <td className="px-3 py-2 font-semibold text-ink uppercase">{v.size}</td>
                    <td className="px-3 py-2 text-ink-2 font-mono text-xs">{v.sku}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.5" defaultValue={v.price} onBlur={(e) => { const n = parseFloat(e.target.value); if (!Number.isNaN(n) && n.toFixed(2) !== v.price) patchVariant(v.id, { price: n }) }}
                        className="w-24 px-2 py-1 bg-white border border-border rounded text-ink text-sm focus:outline-none focus:border-gold text-right" />
                    </td>
                    <td className="px-3 py-2 text-center"><input type="checkbox" checked={v.isActive} onChange={(e) => patchVariant(v.id, { isActive: e.target.checked })} /></td>
                    <td className="px-3 py-2 text-right"><button onClick={() => removeVariant(v.id)} className="text-ink-3 hover:text-red-600">×</button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border">
                  <td className="px-3 py-2"><input value={newVariant.size} onChange={(e) => setNewVariant({ ...newVariant, size: e.target.value.toUpperCase() })} placeholder="XS" className="w-16 px-2 py-1 bg-white border border-border rounded text-ink text-sm focus:outline-none focus:border-gold" /></td>
                  <td className="px-3 py-2 text-xs text-ink-3">(auto)</td>
                  <td className="px-3 py-2 text-right"><input type="number" step="0.5" value={newVariant.price} onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })} placeholder="0.00" className="w-24 px-2 py-1 bg-white border border-border rounded text-ink text-sm focus:outline-none focus:border-gold text-right" /></td>
                  <td colSpan={2} className="px-3 py-2 text-right"><button onClick={addVariant} className="btn-gold text-xs px-3 py-1.5">+ Add size</button></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* AI references — purely a rex-only panel; hidden for everyone else.
              Also globally gated by AI_TOOLS_ENABLED so the whole AI surface
              area can be hidden in one place. */}
          {AI_TOOLS_ENABLED && <AiReferencesPanel productId={data.item.id} productName={data.item.name} />}
        </div>
      </div>
    </div>
  )
}
